import React, { useState, useEffect, useRef } from 'react'
import { Command } from 'lucide-react'

interface CommandItem {
  id: string
  label: string
  description?: string
  action: () => void
  icon?: React.ReactNode
}

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  commands: CommandItem[]
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  commands
}) => {
  const [search, setSearch] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const filteredCommands = commands.filter(cmd =>
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.description?.toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    if (isOpen) {
      setSearch('')
      setSelectedIndex(0)
      inputRef.current?.focus()
    }
  }, [isOpen])

  // Add global ESC key listener when modal is open
  useEffect(() => {
    if (!isOpen) return

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    // Add event listener to document
    document.addEventListener('keydown', handleGlobalKeyDown)
    
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    setSelectedIndex(0)
  }, [search])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action()
          onClose()
        }
        break
      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }

  const handleItemClick = (command: CommandItem) => {
    command.action()
    onClose()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Close modal if clicked on backdrop (not on the modal content)
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-lg mx-4 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div className="flex items-center px-4 py-3 border-b border-gray-200">
          <Command className="w-5 h-5 text-gray-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command or search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 outline-none text-sm placeholder-gray-500"
          />
          <div className="text-xs text-gray-400 ml-2">
            <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 rounded">ESC</kbd>
          </div>
        </div>

        <div ref={listRef} className="max-h-96 overflow-y-auto">
          {filteredCommands.length > 0 ? (
            filteredCommands.map((command, index) => (
              <div
                key={command.id}
                onClick={() => handleItemClick(command)}
                className={`flex items-center px-4 py-3 cursor-pointer transition-colors ${
                  index === selectedIndex
                    ? 'bg-blue-50 border-r-2 border-blue-500'
                    : 'hover:bg-gray-50'
                }`}
              >
                {command.icon && (
                  <div className="w-5 h-5 mr-3 text-gray-400">
                    {command.icon}
                  </div>
                )}
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {command.label}
                  </div>
                  {command.description && (
                    <div className="text-xs text-gray-500 mt-1">
                      {command.description}
                    </div>
                  )}
                </div>
                {index === selectedIndex && (
                  <div className="text-xs text-gray-400 ml-2">
                    <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 rounded">↵</kbd>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <Command className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No commands found</p>
              <p className="text-xs text-gray-400 mt-1">
                Try a different search term
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CommandPalette